import { ChfSymptomLinkId, VITALL_TASK_TYPE_SYSTEM, YesNoCode } from "@/utils/fhir-constants";

import { fhirR4 } from "@smile-cdr/fhirts";

export interface ParsedQuestionnaireItem {
  linkId: string;
  answer: string;
}

export interface QuestionnaireScore {
  yes: number;
  total: number;
}

/**
 * Parses a QuestionnaireResponse into a flat list of questions and answers.
 * Handles standard FHIR item[] structure with valueCoding yes/no answers.
 */
export const parseQuestionnaireResponse = (qr: fhirR4.QuestionnaireResponse): ParsedQuestionnaireItem[] => {
  const items: ParsedQuestionnaireItem[] = [];

  const processItem = (item: fhirR4.QuestionnaireResponseItem) => {
    if (item.answer && item.answer.length > 0) {
      const answer = item.answer[0];
      let answerText: string;

      if (answer.valueCoding) {
        answerText = answer.valueCoding.code?.toUpperCase() === YesNoCode.Yes ? YesNoCode.Yes : YesNoCode.No;
      } else if (answer.valueString) {
        answerText = answer.valueString;
      } else if (answer.valueBoolean !== undefined) {
        answerText = answer.valueBoolean ? YesNoCode.Yes : YesNoCode.No;
      } else if (answer.valueInteger !== undefined) {
        answerText = String(answer.valueInteger);
      } else {
        answerText = YesNoCode.No;
      }

      items.push({
        linkId: item.linkId || "",
        answer: answerText,
      });
    }

    // Recursively process nested group items
    if (item.item) {
      item.item.forEach(processItem);
    }
  };

  // Handle standard FHIR item[] format
  qr.item?.forEach(processItem);

  return items;
};

/**
 * Gets the score (count of "yes" answers / total) from a QuestionnaireResponse.
 */
export const getQuestionnaireScore = (qr: fhirR4.QuestionnaireResponse): QuestionnaireScore => {
  const items = parseQuestionnaireResponse(qr);
  const yesNoItems = items.filter(i => i.answer === YesNoCode.Yes || i.answer === YesNoCode.No);
  const pillowItem = items.find(i => i.linkId === ChfSymptomLinkId.NumberOfPillows);
  const pillowCount = pillowItem ? parseInt(pillowItem.answer, 10) : 0;
  const yesCount = yesNoItems.filter(i => i.answer === YesNoCode.Yes).length + (pillowCount > 0 ? 1 : 0);
  const total = yesNoItems.length + (pillowItem ? 1 : 0);
  return { yes: yesCount, total };
};

/**
 * Determines the alert card type from a Task's code using the vitall.com/documentation system.
 * Returns the code string (e.g. "congestive-heart-failure") or undefined.
 */
export const getAlertCardType = (task: fhirR4.Task): string | undefined => {
  return task.code?.coding?.find(c => c.system === VITALL_TASK_TYPE_SYSTEM)?.code;
};
